import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { Config } from '@wheere/api-gateway';

/**
 * V7 read-only settings card for the integrator gateway detail page.
 *
 * <p>Why a dedicated component instead of reusing inside-gateways' editors? The inside UX is
 * built for admin troubleshooting (raw JSON editor, validation actions). The integrator just
 * needs to *see* what's configured — never mutate. We expose three sub-cards (signal /
 * attenuator / clock) with the JSON pretty-printed and a small "applied state" chip per
 * config, derived from {@code Config.stateSignal/stateAttenuator/stateClock}.
 *
 * <p>The visual final layout will be polished against the Pencil HTML — for now this is
 * functional.
 */
@Component({
  selector: 'lib-settings-readonly',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="grid grid-cols-1 gap-3 lg:grid-cols-3">
      @for (section of sections(); track section.label) {
        <section class="flex flex-col gap-2 rounded-lg border border-gray-200 bg-white p-3">
          <header class="flex items-center justify-between">
            <h3 class="text-sm font-medium text-gray-700">{{ section.label }}</h3>
            <span
              class="rounded-full px-2 py-0.5 text-xs font-medium uppercase"
              [class.bg-aquamarine-50]="section.applied"
              [class.text-aquamarine-900]="section.applied"
              [class.bg-gray-100]="!section.applied"
              [class.text-gray-600]="!section.applied">
              {{ section.applied ? 'Applied' : 'Pending' }}
            </span>
          </header>
          <pre class="m-0 max-h-72 overflow-auto rounded bg-gray-50 p-2 font-mono text-xs leading-snug text-gray-700">{{
            section.content
          }}</pre>
        </section>
      } @empty {
        <p class="col-span-full text-sm text-gray-500">No configuration recorded for this gateway.</p>
      }
    </div>
  `,
})
export class SettingsReadonlyComponent {
  config = input.required<Config | null | undefined>();

  protected readonly sections = computed(() => {
    const c = this.config();
    if (!c) return [];
    return [
      {
        label: 'Signal',
        content: prettyJson(c.signalConfig),
        applied: hasContent(c.stateSignal),
      },
      {
        label: 'Attenuator',
        content: prettyJson(c.attenuatorConfig),
        applied: hasContent(c.stateAttenuator),
      },
      {
        label: 'Clock',
        content: prettyJson(c.clockConfig),
        applied: hasContent(c.stateClock),
      },
    ].filter(s => s.content);
  });
}

function prettyJson(raw: string | undefined): string {
  if (!raw) return '';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    // Not valid JSON — show the raw string. Better than hiding.
    return raw;
  }
}

function hasContent(s: string | undefined): boolean {
  return !!s && s.trim().length > 0;
}
